import * as React from 'react';
import { useEffect, useReducer } from 'react';
import { useLocation } from 'react-router-dom';
import AppShell from './AppShell';
import EventShell from './EventShell';
import useEventChrome from './useEventChrome';
import { OVERRIDES_EVENT, isV3World } from '../flags';
import '../bridge.css';

/*
 * Wraps classic and v2 pages in the v3 rail (event routes get the event
 * rail). Legacy content sits in .psv3-legacy so v3 resets and shared
 * class names (row, dot, link) don't leak into it.
 */

const EVENT_PATH = /^\/(?:v2\/)?activations\/(\d+)(?:\/([^/]+))?/;

/* routes whose v3 page already brings its own shell */
const OWNS_SHELL = [
    /^\/home\/?$/,
    /^\/activations\/?$/,
    /^\/activations\/\d+\/?$/,
    /^\/activations\/\d+\/sharing(\/methods)?\/?$/,
    /^\/kit\/?$/,
    /^\/rules\/?$/,
    /^\/preview\/?$/,
    /^\/(staff|gear|venues|contacts)\/?$/,
    /^\/device-management\/?$/,
    /^\/pipeline(\/\d+|\/settings)?\/?$/,
    /^\/design\/?$/,
    /^\/activations\/create\/?$/,
    /^\/activations\/\d+\/edit\/?$/,
    /^\/jobs\/\d+\/activation\/create\/?$/,
    /^\/activations\/\d+\/(shares|analyze|payments|capture|insights(\/session\/[^/]+)?|gallery|processing(\/new|\/\d+\/edit)?|delivery|production|media)\/?$/,
    /^\/device-management\/(device-keys\/create|device-groups\/create|device-invites\/create|device-groups\/\d+\/(invites|device-keys(\/assign|\/create)?))\/?$/,
    /^\/orgs\/\d+\/device-telemetry\/[^/]+\/?$/,
    /^\/org(\/(stats|accounts|whitelabel|templates|integrations))?\/?$/,
    /^\/admin(\/(orgs\/\d+|licenses|features|templates|announcements|global-settings|system-health|gallery-users|stats|sync))?\/?$/,
];

export function ownsShell(pathname: string): boolean {
    return OWNS_SHELL.some((re) => re.test(pathname));
}

/** Re-render when the dev toolbar flips the world. */
export function useWorld(flagOn: boolean): boolean {
    const [, bump] = useReducer((x: number) => x + 1, 0);
    useEffect(() => {
        window.addEventListener(OVERRIDES_EVENT, bump);
        window.addEventListener('storage', bump);
        return () => {
            window.removeEventListener(OVERRIDES_EVENT, bump);
            window.removeEventListener('storage', bump);
        };
    }, []);
    return isV3World(flagOn);
}

const SECTION_OF: Record<string, string> = {
    gallery: 'gallery',
    capture: 'capture',
    processing: 'capture',
    workflows: 'capture',
    media: 'media',
    analyze: 'media',
    sharing: 'sharing',
    shares: 'sharing',
    'manage-shares': 'sharing',
    galleryConfiguration: 'gallery',
    sessions: 'sharing',
    insights: 'insights',
    payments: 'payments',
};

function EventFrame({
    id,
    section,
    children,
}: {
    id: number;
    section?: string;
    children: React.ReactNode;
}) {
    const chrome = useEventChrome(id);
    return (
        <EventShell
            orgInitials={chrome.orgInitials}
            orgLogo={chrome.orgLogo}
            eventName={chrome.eventName}
            eventSub={chrome.eventSub}
            live={chrome.live}
            quickLinks={chrome.quickLinks}
            nav={chrome.nav}
            activeSection={(section && SECTION_OF[section]) || 'overview'}
        >
            {children}
        </EventShell>
    );
}

export default function UnifiedFrame({
    children,
}: {
    children: React.ReactNode;
}) {
    const { pathname } = useLocation();
    const inner = <div className="psv3-legacy">{children}</div>;
    const m = pathname.match(EVENT_PATH);
    if (m) {
        return (
            <EventFrame id={Number(m[1])} section={m[2]}>
                {inner}
            </EventFrame>
        );
    }
    return <AppShell>{inner}</AppShell>;
}
